import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

// API
import { getProjectSettingList } from "Network/Core/Booking/booking";
import { getLocalStorage, setLocalStorage } from "Network/ApiService";

// component
import Button from "Components/UI/Button";
import images from "Assets/images";

// Redux
import { setProjectSetting } from "Store/Actions/ProjectManageMent/SiteActionCreator";

// helper
import { constants, ls_vars } from "Library/Constants";
import tabs, { languageOption } from "StaticData/Dashboard/tabs";

// css
import "./style.css";
import "Assets/css/customize.css";

// Static Data
import { useProjectSettings } from "Hooks/useProjectSettings";
import { useRolePermission } from "Hooks/useRolePermission";
import { useApiCall } from "Hooks/useApiCall";
import { useNav } from "Hooks/useNav";
import { resetBookingDetails } from "Store/Actions/BookingModule/bookingActionCreator";
import { setEmissionSettings } from "Store/Actions/ProjectSettings/EmissionSettingsCreator";

export type featureFlagResponse = {
  data: {
    id: number;
    feature: {
      id: number;
      code: string;
      name: string;
      description: string;
      createdAt: string;
      modifiedAt: string;
    };
    region: {
      id: number;
      name: "us-west-2";
    };
    enabled: boolean;
    createdAt: string;
    modifiedAt: string;
  }[];
  status: number;
  statusText: string;
};

export interface TabsBaseType {
  id: number;
  name: string;
  image: string;
  getUrl?: (ref: string) => string;
  url: string;
  isActive?: boolean;
  isAvailable?: boolean;
  isBottomTab?: boolean;
  isDisabled?: boolean;
  isOpen?: boolean;
  data?: TabsBaseType[]; // Make it recursive to support unlimited nesting
}

const index = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { call } = useApiCall();
  const { viewBookingForm, viewRegisterEquipment } = useNav();
  const { checkPermission } = useRolePermission();
  const { checkSetting, keys } = useProjectSettings();
  const { featureFlags } = useSelector((state: any) => state.auth);
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  const regionId = getLocalStorage(ls_vars.REGION_ID);
  const isCreateBookingVisible = checkPermission(["booking:create"]);
  const isCalendarVisible = checkPermission(["screen:calendar"]);
  const isViewBookingVisible = checkPermission(["booking:view"]);
  const isUserManagementVisible = checkPermission(["screen:user:management"]);
  const isProjectManagementVisible = checkPermission([
    "screen:project:management",
  ]);
  const isEquipmentMasterListVisible = checkPermission(["equipment:view"]);
  const isRegisterEquipmentVisible = checkPermission([
    "register:equipment:view",
  ]);
  const isEquipmentUsageVisible = checkPermission(["equipment:usage:view"]);
  const isVehicleEmissionVisible = checkPermission(["emissions:view"]);
  const isReportsVisible = checkPermission(["emissions:reports:view"]);
  const isVehicleTrackingVisible =
    checkPermission(["screen:vehicle:tracking"]) &&
    checkSetting(keys.VIEW_VEHICLE_TRACKING);
  const isProjectSettingsVisible = checkSetting(keys.VIEW_PROJECT_SETTINGS);
  const isProjectSettingsAvailable =
    isProjectSettingsVisible && featureFlags.FLAG_PROJECT_SETTINGS[regionId];

  const [sidebarTabs, setSidebarTabs] = useState<TabsBaseType[]>(tabs);

  const [collapseTab, setCollapseTab] = useState(false);
  const options = languageOption;
  const [selectedOption, setSelectedOption] = useState(
    languageOption[0]?.value || ""
  );

  const emissionSettingFromState = useSelector(
    (state: any) => state?.emissionSettingsReducer?.isEmissionsEnabled
  );
  const emissionSettingFromProject = checkSetting(keys.EMISSIONS_SETTINGS);
  const [isEnvironmentModel, setIsEnvironmentModel] = useState(
    emissionSettingFromProject || emissionSettingFromState
  );
  const emissionSettingsReduxState = useSelector(
    (state: any) => state?.emissionSettingsReducer?.isEmissionsEnabled
  );

  const checkAvailability = (route: string) => {
    switch (route) {
      case "booking_list":
        return isViewBookingVisible;
      case "calendar":
        return isCalendarVisible;
      case "project_management":
        return isProjectManagementVisible;
      case "user_management":
        return isUserManagementVisible;
      case "project_setting":
        return isProjectSettingsAvailable;
      case "vehicle_tracking":
        return isVehicleTrackingVisible;
      case "equipment_master":
        return isEquipmentMasterListVisible;
      case "list_equipments":
        return isRegisterEquipmentVisible;
      case "equipment_management":
      case "equipment_usage":
        return isEquipmentUsageVisible;
      case "vehicle_emission":
        return isVehicleEmissionVisible;
      case "global_settings":
        return isProjectManagementVisible;
      case "analytics":
      case "summary_emissions_report":
      case "vehicle_emission_reports":
      case "equipment_and_vehicle_emissions":
        return isReportsVisible;
      default:
        return true;
    }
  };

  useEffect(() => {
    getProjectSetting();
  }, []);

  useEffect(() => {
    const isValueMissing = Boolean(
      null === getLocalStorage(ls_vars.USER_IS_ADMIN)
    );
  }, []);

  useEffect(() => {
    const newValue = emissionSettingFromProject || emissionSettingFromState;
    setIsEnvironmentModel(newValue);
  }, [emissionSettingFromProject, emissionSettingFromState]);

  useEffect(() => {
    if (projectRef) {
      setSidebarTabs((prevTabs) =>
        prevTabs.map((item) => {
          const activeTab = location.pathname;
          const url = item?.getUrl ? item.getUrl(projectRef) : "";

          // Update isAvailable based on emission settings
          let isAvailable = checkAvailability(item.name);
          if (!isEnvironmentModel && [6, 7, 8, 9].includes(item.id)) {
            isAvailable = false;
          }

          const nestedData = item?.data?.map((nestedEl) => {
            const nestedUrl = nestedEl?.getUrl
              ? nestedEl.getUrl(projectRef)
              : "";
            const nestedIsActive = activeTab.includes(nestedUrl);

            // Check if this tab should be initially open
            const hasActiveChild = nestedEl.data?.some((child) =>
              activeTab.includes(child.getUrl ? child.getUrl(projectRef) : "")
            );

            // Update nested isAvailable based on emission settings
            let nestedIsAvailable = checkAvailability(nestedEl.name);
            if (!isEnvironmentModel && item.id === 5 && [4, 5, 6].includes(nestedEl.id)) {
              nestedIsAvailable = false;
            }

            return {
              ...nestedEl,
              url: nestedUrl,
              isActive: activeTab === nestedUrl,
              // Only open if this tab or its children are active
              isOpen: nestedIsActive || hasActiveChild,
              isAvailable: nestedIsAvailable,
              data: nestedEl.data?.map((deepEl) => {
                const deepUrl = deepEl.getUrl
                  ? deepEl.getUrl(projectRef)
                  : "";
                const isDeepActive = activeTab.includes(deepUrl);
                return {
                  ...deepEl,
                  isActive: isDeepActive,
                  // Only open if active
                  isOpen: isDeepActive,
                  url: deepUrl,
                };
              }),
            };
          });

          // Check if any nested item is active
          const isOneOfTheNestedActive = nestedData?.some(
            (el) => el?.isActive || el.data?.some((child) => child.isActive)
          );

          return {
            ...item,
            data: nestedData,
            isActive: !url ? isOneOfTheNestedActive : activeTab.includes(url),
            // Only open if this tab or its children are active
            isOpen: isOneOfTheNestedActive || item.isOpen,
            isAvailable: isAvailable,
            url: url,
          };
        })
      );
    }
  }, [
    projectRef,
    location.pathname,
    isProjectManagementVisible,
    isUserManagementVisible,
    isProjectSettingsAvailable,
    isVehicleTrackingVisible,
    emissionSettingFromState,
    emissionSettingFromProject,
    isEnvironmentModel,
  ]);


  const onCollapse = () => {
    setCollapseTab(!collapseTab);
  };

  const onNestedTabClick = (targetName: string, parentName?: string) => {
    setSidebarTabs((prevTabs: TabsBaseType[]) =>
      prevTabs.map((el) => {
        // Toggle the clicked parent tab
        if (el.name === targetName) {
          return {
            ...el,
            isOpen: !el.isOpen,
            data: el.data?.map((subEl) => ({
              ...subEl,
              isActive: false,
            })),
          };
        }

        // Handle nested tabs
        if (el.data) {
          return {
            ...el,
            data: el.data.map((subEl) => {
              // If this is the parent that was clicked
              if (subEl.name === targetName) {
                return {
                  ...subEl,
                  isOpen: !subEl.isOpen,
                  isActive: false,
                  data: subEl.data?.map((deepEl) => ({
                    ...deepEl,
                    isActive: false,
                  })),
                };
              }

              // If this contains the clicked item
              if (subEl.data?.some((deepEl) => deepEl.name === targetName)) {
                return {
                  ...subEl,
                  isOpen: true,
                  data: subEl.data.map((deepEl) => ({
                    ...deepEl,
                    isActive: deepEl.name === targetName,
                    isOpen:
                      deepEl.name === targetName
                        ? !deepEl.isOpen
                        : deepEl.isOpen,
                  })),
                };
              }

              return subEl;
            }),
          };
        }

        return el;
      })
    );
  };

  const getProjectSetting = async () => {
    call(
      () => getProjectSettingList(projectRef),
      (data) => {
        if (data?.results) {
          setLocalStorage(
            ls_vars.PROJECT_SETTINGS,
            JSON.stringify(data.results)
          );
          const timeFormat = data.results.find(
            (item: { setting: number }) => item.setting === 7
          )?.value;
          const emission_setting = data?.results?.find(
            (item: { setting: number }) => item.setting === 11
          )?.value;
          dispatch(setEmissionSettings(emission_setting));
          dispatch(setProjectSetting(timeFormat));
        }
      }
    );
  };

  const renderTabs = () =>
    sidebarTabs.map((item) => {
      const isVisible = item.isAvailable;
      const isNested = item.data?.length;
      const isOpen = isNested && item.isOpen;

      if (!isVisible) return null;

      if (item.isBottomTab) {
        return (
          <li className="nav-item bottom-links" key={item.id}>
            <div
              className={
                collapseTab
                  ? "form-group mb-0 toggled"
                  : "form-group mb-0 toggled"
              }
            >
              <Select
                classNamePrefix="form-control-language"
                options={options}
                value={
                  options.find((c) => c.value === selectedOption) || options[0]
                }
                onChange={(val: any) => setSelectedOption(val?.value || "")}
                defaultValue={options[0]}
              />
            </div>
          </li>
        );
      }

      if (isNested) {
        return (
          <li
            key={item.id}
            className={
              item?.isActive && !item?.isDisabled
                ? `nav-item active ${item?.isDisabled ? "disable" : ""}`
                : `nav-item ${item?.isDisabled ? "disable" : ""}`
            }
          >
            <Link
              className={`nav-link ${item.isActive ? "active" : ""} collapse`}
              to=""
              data-toggle="collapse"
              onClick={() => onNestedTabClick(item.name, undefined)}
              aria-expanded="true"
              aria-controls="collapseTwo"
            >
              <img alt={item.name} src={item.image} />
              <span title={t(`Translation.${item.name}`)}>{t(`Translation.${item.name}`)}</span>
            </Link>

            {isOpen && (
              <div
                id="collapseTwo"
                className="collapse"
                style={{ display: "block" }}
              >
                <div className="collapse-inner rounded">
                  {item.data?.map(
                    (data) =>
                      data?.isAvailable && (
                        <div key={data.id} className="pl-1">
                          {data.data?.length ? (
                            <>
                              <Link
                                className={`nav-link ${data.isActive ? "active" : ""
                                  } nested-parent`}
                                to=""
                                onClick={(e) => {
                                  e.preventDefault();
                                  onNestedTabClick(data.name, item.name);
                                }}
                              >
                                <img src={data.image} />
                                <span title={t(`Translation.${data.name}`)}>{t(`Translation.${data.name}`)}</span>
                              </Link>
                              {data.isOpen && (
                                <div className="nested-items pl-2">
                                  {data.data.map(
                                    (nestedData) =>
                                      nestedData?.isAvailable && (
                                        <div key={nestedData.id}>
                                          {nestedData.data?.length ? (
                                            <>
                                              // For nested items
                                              <Link
                                                className={`nav-link ${nestedData.isActive
                                                    ? "active"
                                                    : ""
                                                  } nested`}
                                                to={
                                                  nestedData.getUrl
                                                    ? nestedData.getUrl(
                                                      projectRef
                                                    )
                                                    : nestedData.url || ""
                                                }
                                                onClick={(e) => {
                                                  e.preventDefault(); // Always prevent default first
                                                  onNestedTabClick(
                                                    nestedData.name,
                                                    data.name
                                                  );

                                                  // If it's a leaf node (no children), handle navigation
                                                  if (
                                                    !nestedData.data?.length
                                                  ) {
                                                    const url =
                                                      nestedData.getUrl
                                                        ? nestedData.getUrl(
                                                          projectRef
                                                        )
                                                        : nestedData.url;
                                                    if (url) {
                                                      navigate(url);
                                                    }
                                                  }
                                                }}
                                              >
                                                <img src={nestedData.image} />
                                                <span title={t(`Translation.${nestedData.name}`)}>
                                                  {t(
                                                    `Translation.${nestedData.name}`
                                                  )}
                                                </span>
                                              </Link>
                                              {nestedData.isOpen && (
                                                <div className="nested-sub-items">
                                                  {nestedData.data.map(
                                                    (subNestedData) =>
                                                      subNestedData?.isAvailable && (
                                                        // For deepest level items
                                                        <Link
                                                          className={`nav-link ${nestedData.isActive
                                                              ? "active"
                                                              : ""
                                                            } nested`}
                                                          to=""
                                                          onClick={(e) => {
                                                            e.preventDefault();
                                                            // Always pass the parent name
                                                            onNestedTabClick(
                                                              nestedData.name,
                                                              data.name
                                                            );
                                                          }}
                                                        >
                                                          <img
                                                            src={
                                                              nestedData.image
                                                            }
                                                          />
                                                          <span title={t(`Translation.${nestedData.name}`)}>
                                                            {t(
                                                              `Translation.${nestedData.name}`
                                                            )}
                                                          </span>
                                                        </Link>
                                                      )
                                                  )}
                                                </div>
                                              )}
                                            </>
                                          ) : (
                                            <Link
                                              className={`nav-link ${nestedData.isActive
                                                  ? "active"
                                                  : ""
                                                } nested`}
                                              to={
                                                nestedData.getUrl
                                                  ? nestedData.getUrl(
                                                    projectRef
                                                  )
                                                  : nestedData.url || ""
                                              }
                                              onClick={(e) => {
                                                // Always pass the parent name
                                                onNestedTabClick(
                                                  nestedData.name,
                                                  data.name
                                                );
                                                if (nestedData.data?.length) {
                                                  e.preventDefault();
                                                }
                                              }}
                                            >
                                              <img src={nestedData.image} />
                                              <span title={t(`Translation.${nestedData.name}`)}>
                                                {t(
                                                  `Translation.${nestedData.name}`
                                                )}
                                              </span>
                                            </Link>
                                          )}
                                        </div>
                                      )
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <Link
                              className={
                                data.isActive ? `nav-link active` : `nav-link`
                              }
                              to={
                                data.getUrl
                                  ? data.getUrl(projectRef)
                                  : data.url || ""
                              }
                              onClick={() => onNestedTabClick(data.name)}
                            >
                              <img src={data.image} />
                              <span title={t(`Translation.${data.name}`)}>{t(`Translation.${data.name}`)}</span>
                            </Link>
                          )}
                        </div>
                      )
                  )}
                </div>
              </div>
            )}
          </li>
        );
      }

      return (
        <div key={item.id}>
          <li
            className={
              item?.isActive && !item?.isDisabled
                ? `nav-item active ${item?.isDisabled ? "disable" : ""}`
                : `nav-item ${item?.isDisabled ? "disable" : ""}`
            }
          >
            {item?.isDisabled && (
              <div className="coming-soon-tooltip">
                <p className="">{t("Translation.coming_soon")}</p>
              </div>
            )}
            <Link
              to={!item?.isDisabled ? item.url : ""}
              className={item.isActive ? `nav-link active` : `nav-link`}
            >
              <img alt={item.name} src={item.image} />
              <span title={t(`Translation.${item.name}`)}>{t(`Translation.${item.name}`)}</span>
            </Link>
          </li>
        </div>
      );
    });

  const handleNavigateNewBooking = () => {
    viewBookingForm(projectRef);
    dispatch(resetBookingDetails());
  };

  const handleNavigateEquipmentRegistration = () => {
    viewRegisterEquipment(projectRef);
  };

  const activeTab = location.pathname;
  const isOnBookingDetails = activeTab.includes("booking-form");
  const isEquipmentList = activeTab.includes("equipment");
  return (
    <>
      <ul
        className={
          collapseTab
            ? "navbar-nav sidebar accordion toggled"
            : "navbar-nav sidebar accordion"
        }
        id="accordionSidebar"
      >
        {/* <!-- Sidebar - Brand --> */}

        {(isCreateBookingVisible ||
          checkPermission(["register:equipment:create"])) && (
            <>
              {isEquipmentList ? (
                collapseTab ? (
                  <Button
                    handleClick={handleNavigateEquipmentRegistration}
                    className="btn theme-btn add-booking plusicon_button toggled"
                    buttonLabel={
                      <span>
                        <img src={images.plusIcon} alt="plus" />
                      </span>
                    }
                  />
                ) : (
                  <Button
                    handleClick={handleNavigateEquipmentRegistration}
                    className="btn theme-btn add-equipment"
                    buttonLabel={
                      <span>
                        <img src={images.plusIcon} alt="plus" />
                        <span className="booking-span">
                          {t("Translation.register_equipment")}
                        </span>
                      </span>
                    }
                  />
                )
              ) : collapseTab ? (
                <Button
                  handleClick={handleNavigateNewBooking}
                  className="btn theme-btn add-booking plusicon_button toggled"
                  disabled={isOnBookingDetails}
                  buttonLabel={
                    <span>
                      <img src={images.plusIcon} alt="plus" />
                    </span>
                  }
                />
              ) : (
                <Button
                  handleClick={handleNavigateNewBooking}
                  className="btn theme-btn add-booking"
                  disabled={isOnBookingDetails}
                  buttonLabel={
                    <span>
                      <img src={images.plusIcon} alt="plus" />
                      <span className="booking-span">
                        {t("Translation.new_booking")}
                      </span>
                    </span>
                  }
                />
              )}
            </>
          )}

        {renderTabs()}

        <li key="collapse" className="nav-item">
          <Link
            to=""
            onClick={() => onCollapse()}
            className="nav-link collapse"
          >
            <img alt="collapse" src={images.collapse} />
            <span title={t("Translation.collapse")}>{t("Translation.collapse")}</span>
          </Link>
        </li>
      </ul>
    </>
  );
};

export default index;
